import React, { useState } from "react";

import { Pagination } from "@performyard/py-components";
import type { SortChangedEvent } from "@performyard/py-components";
import { ASYNC_TASK_NAMES } from "@performyard/service-contracts";
import type { CompetencyAssignmentJSON, CompetencyDict } from "@performyard/service-contracts";

import DownloadButtonSSE from "../../../../../components.smart/DownloadButtonSSE";
import FilterChips from "../../../../../components/FilterChips";
import PageHeading from "../../../../../components/PageHeading";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import { BulkEditBar } from "../../../components/BulkEditBar";
import { BulkEditToggleButton } from "../../../components/BulkEditToggleButton";
import CompetenciesFilterPanel from "../../../components/CompetenciesFilterPanel";
import { type CompetenciesFilter } from "../../../components/CompetenciesFilterPanel/schema";
import {
	convertFiltersToFilterChips,
	getActiveFiltersCount,
	getNonDismissibleChipIds,
	removeFilter,
} from "../../../components/CompetenciesFilterPanel/utils";
import CompetenciesTable from "../../../components/CompetenciesTable";
import DiscardChangesModal from "../../../components/DiscardChangesModal";
import UpdateCurrentLevelModal from "../../../components/UpdateCurrentLevelModal";
import ViewHistoryPanel from "../../../components/ViewHistoryPanel";
import useBulkLevelEdit, { useBulkLevelEditNavGuard } from "../../../components/useBulkLevelEdit";

import { useBulkUpdateAssignmentCurrentLevels, useCompetencyAssignments } from "../../../../../api/hooks";
import { useIsNewNavigation } from "../../../../../api/hooks/defs/navigation";
import { useUserAsyncTask } from "../../../../../hooks/useUserAsyncTask";
import useCompetencyAssignmentActions from "../../../hooks/useCompetencyAssignmentActions";

import { requestCompetencyAssignmentsCsv } from "../../../../../api/services";
import type { ListCompetencyAssignmentsParams } from "../../../../../api/services";

import { competencySortFromEvent } from "../../../utils";

import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { PAGE_SIZE, colIdToSortBy } from "./schema";

const PAGE_TITLE = "My Competencies";

type MyCompetenciesProps = {
	employeeId: string;
	employeeName?: string | null;
	navLinks: SideNavigationLink[];
	activePage: string;
	canUpdateCurrentLevel: boolean;
	canExportCsv: boolean;
};

const MyCompetencies = ({
	employeeId,
	employeeName,
	navLinks,
	activePage,
	canUpdateCurrentLevel,
	canExportCsv,
}: MyCompetenciesProps): React.ReactElement => {
	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<CompetenciesFilter>({
		defaultSortName: "competency_name",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
	});

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);
	const isNewNav = useIsNewNavigation();

	const params: ListCompetencyAssignmentsParams = {
		employeeId,
		sortBy: colIdToSortBy(sort.name) ?? "competency_name",
		sortOrder: sort.direction,
		status: filters.status,
		source: filters.source,
		levelGap: filters.levelGap,
		search: debouncedSearchValue || undefined,
		limit: PAGE_SIZE,
		skip: (pageNumber - 1) * PAGE_SIZE,
	};

	const { data, isLoading } = useCompetencyAssignments(params, {
		placeholderData: (previousData) => previousData,
	});

	const { task: exportTask, triggerTask: triggerExport } = useUserAsyncTask(
		ASYNC_TASK_NAMES.EXPORT_COMPETENCY_ASSIGNMENTS_CSV
	);

	const assignments: CompetencyAssignmentJSON[] = data?.assignments ?? [];
	const competencies: CompetencyDict = data?.competencies ?? {};
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / PAGE_SIZE);

	const {
		openPanel,
		actionSelectedRow,
		isUpdateModalOpen,
		onOpenPanel,
		closePanel,
		onUpdateLevel,
		closeUpdateModal,
	} = useCompetencyAssignmentActions();

	const activeCompetency = actionSelectedRow ? competencies[actionSelectedRow.competency_id] : undefined;

	const onSortChanged = (event: SortChangedEvent<CompetencyAssignmentJSON>) => {
		const nextSort = competencySortFromEvent(event);
		if (nextSort) setSort(nextSort);
	};

	const bulkUpdateCurrentLevels = useBulkUpdateAssignmentCurrentLevels();
	const bulkLevelEdit = useBulkLevelEdit({
		onSave: async (edits) => {
			const response = await bulkUpdateCurrentLevels.mutateAsync(
				edits.map((edit) => ({ assignment_id: edit.id, current_level: edit.value }))
			);
			return { successfulIds: response.successful, failedIds: response.failed.map((f) => f.assignment_id) };
		},
		successMessage: "Current levels updated",
		failureMessage: (count) => `${count} current level${count === 1 ? "" : "s"} failed to update`,
		errorMessage: "Failed to update current levels",
		scopeKey: employeeId,
	});
	useBulkLevelEditNavGuard(bulkLevelEdit.pendingCount);

	const mainContent = (
		<div>
			<BulkEditBar
				isOpen={bulkLevelEdit.editMode}
				pendingCount={bulkLevelEdit.pendingCount}
				atCap={bulkLevelEdit.atCap}
				isSaving={bulkLevelEdit.isSaving}
				onCancel={bulkLevelEdit.requestExit}
				onSave={() => void bulkLevelEdit.save()}
			/>
			<div className="flex">
				<CompetenciesTable
					assignments={assignments}
					competencies={competencies}
					loading={isLoading}
					canUpdateCurrentLevel={canUpdateCurrentLevel}
					sort={sort}
					onSortChanged={onSortChanged}
					onOpenPanel={onOpenPanel}
					onUpdateLevel={onUpdateLevel}
					editMode={bulkLevelEdit.editMode}
					pendingLevels={bulkLevelEdit.pendingEdits}
					onLevelChange={bulkLevelEdit.setPendingEdit}
					isSaving={bulkLevelEdit.isSaving}
					atCap={bulkLevelEdit.atCap}
				/>
			</div>
			{pageCount > 1 && (
				<div className="mx-auto mt-5 flex w-full max-w-[400px] justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}
		</div>
	);

	const filterChips = convertFiltersToFilterChips(filters);

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(v) => setDebouncedSearch(v)}
			searchPlaceholder="Search competencies..."
			onFilterButtonPress={() => setIsFiltersPanelOpen(true)}
			activeFiltersCount={getActiveFiltersCount(filters)}
		/>
	);

	const drawer = (
		<CompetenciesFilterPanel
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			filters={filters}
			onApplyFilters={(f) => setFilters(f)}
		/>
	);

	const handleDeleteFilter = (id: string) => {
		const nextFilters = removeFilter(filters, id);
		if (nextFilters) setFilters(nextFilters);
	};

	const toolbarInformationRow = (
		<FilterChips
			filters={filterChips}
			label="Filters applied: "
			nonDismissibleFilters={getNonDismissibleChipIds(filters)}
			onDeleteFilter={handleDeleteFilter}
		/>
	);

	// The export honors the view's active filters/search/sort so the CSV matches what's on screen.
	const csvActionButtons = canExportCsv
		? [
				<DownloadButtonSSE
					key="download-csv"
					task={exportTask}
					onClick={() =>
						triggerExport(() =>
							requestCompetencyAssignmentsCsv({
								scope: "employee",
								employeeId,
								search: debouncedSearchValue || undefined,
								status: filters.status,
								source: filters.source,
								levelGap: filters.levelGap,
								sortBy: colIdToSortBy(sort.name) ?? "competency_name",
								sortOrder: sort.direction,
							})
						)
					}
					testid="download-csv"
				>
					Download CSV
				</DownloadButtonSSE>,
			]
		: [];

	const bulkEditButtons = canUpdateCurrentLevel
		? [
				<BulkEditToggleButton
					key="bulk-edit-toggle"
					editMode={bulkLevelEdit.editMode}
					manageLabel="Manage Current Levels"
					editingLabel="Editing Current Levels"
					disabled={bulkLevelEdit.isSaving}
					onEnter={bulkLevelEdit.enterEditMode}
					onRequestExit={bulkLevelEdit.requestExit}
				/>,
			]
		: [];

	const actionButtons = [...csvActionButtons, ...bulkEditButtons];

	const assignmentId = actionSelectedRow?._id ?? "";
	return (
		<>
			<InfoPageTemplate
				employeeID={employeeId}
				pageTitle={employeeName ? `Competencies - ${employeeName} - PerformYard` : "Employee Competencies"}
				pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
				navigationLinks={navLinks}
				activeNavigationLinkKey={activePage}
				actionButtons={actionButtons}
				toolbarActionRow={toolbarActionRow}
				drawer={drawer}
				toolbarInformationRow={toolbarInformationRow}
				mainContent={mainContent}
			/>
			<ViewHistoryPanel
				key={`history-${assignmentId}`}
				isOpen={openPanel === "history"}
				onClose={closePanel}
				assignmentId={assignmentId}
				competency={activeCompetency}
			/>
			<UpdateCurrentLevelModal
				isOpen={isUpdateModalOpen}
				onClose={closeUpdateModal}
				assignment={actionSelectedRow}
				competency={activeCompetency}
				assignee={employeeName ? { name: employeeName } : undefined}
			/>
			<DiscardChangesModal
				isOpen={bulkLevelEdit.confirmingExit}
				pendingCount={bulkLevelEdit.pendingCount}
				onConfirm={bulkLevelEdit.confirmExit}
				onClose={bulkLevelEdit.dismissExit}
			/>
		</>
	);
};

export default MyCompetencies;
